import React, { useEffect, useRef, useState } from 'react';

interface AnimatedPointsProps {
  points: number;
  className?: string;
}

const AnimatedPoints: React.FC<AnimatedPointsProps> = ({ points, className = '' }) => {
  const [displayedPoints, setDisplayedPoints] = useState(points);
  const [increase, setIncrease] = useState<number | null>(null);
  const displayedPointsRef = useRef(points);
  const previousPointsRef = useRef(points);

  useEffect(() => {
    const previousPoints = previousPointsRef.current;
    previousPointsRef.current = points;

    if (points <= previousPoints || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      displayedPointsRef.current = points;
      setDisplayedPoints(points);
      setIncrease(null);
      return;
    }

    const from = displayedPointsRef.current;
    const duration = 700;
    const startedAt = performance.now();
    let animationFrame = 0;
    let clearTimer = 0;

    setIncrease(points - previousPoints);

    const animate = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(from + (points - from) * easedProgress);
      displayedPointsRef.current = nextValue;
      setDisplayedPoints(nextValue);

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(animate);
      } else {
        clearTimer = window.setTimeout(() => setIncrease(null), 350);
      }
    };

    animationFrame = window.requestAnimationFrame(animate);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(clearTimer);
    };
  }, [points]);

  return (
    <span
      className={`navbar-stat navbar-stat--points animated-points${increase !== null ? ' is-increasing' : ''}${className ? ` ${className}` : ''}`}
      aria-label={`${points} points`}
      title={`${points.toLocaleString()} points`}
    >
      {increase !== null && <span key={points} className="animated-points-flash" aria-hidden="true" />}
      <span className="navbar-stat-icon" aria-hidden="true">★</span>
      <span className="navbar-stat-value" aria-hidden="true">{displayedPoints.toLocaleString()}</span>
      <span className="navbar-stat-label" aria-hidden="true">points</span>
      {increase !== null && (
        <span key={`gain-${points}`} className="animated-points-gain" aria-hidden="true">
          +{increase}
        </span>
      )}
    </span>
  );
};

export default AnimatedPoints;
